import { inject, Injectable, Renderer2, RendererFactory2 } from '@angular/core';
import { LoggerService } from '@aruba/common-lib';
import { TranslocoLangDetectorService } from '@shared/transloco/transloco-lang-detector.service';
import { TokenResponse } from 'angular-oauth2-oidc';
import { BehaviorSubject, catchError, of, tap } from 'rxjs';
import { API_GATEWAY_EUROPE_XSRF_TOKEN } from '../../interceptors/oauth-interceptor.constants';
import { UsersService } from '../api/users.service';
import { AppConfigService } from '../app-config.service';
import { AuthService } from './auth.service';

@Injectable()
export class GatewayAuthService extends AuthService {
  private readonly appConfigService = inject(AppConfigService);
  private renderer: Renderer2 = inject(RendererFactory2).createRenderer(null, null);
  private loggerService = inject(LoggerService);
  private readonly usersService = inject(UsersService);
  private readonly translocoLangDetectorService = inject(TranslocoLangDetectorService);

  private readonly isDoneLoadingSubject$ = new BehaviorSubject<boolean>(true);
  public isDoneLoading$ = this.isDoneLoadingSubject$.asObservable();

  private readonly isAuthenticatedSubject$ = new BehaviorSubject<boolean>(this.isLogged());
  public isAuthenticated$ = this.isAuthenticatedSubject$.asObservable();

  constructor() {
    super();
  }

  isLogged(): boolean {
    const hasXsrfToken = document.cookie.includes(API_GATEWAY_EUROPE_XSRF_TOKEN);
    return hasXsrfToken;
  }

  public runInitialLoginSequence(): Promise<void> {
    if (this.isLogged()) {
      this.usersService
        .getLocaleInfo()
        .pipe(
          tap(response => this.translocoLangDetectorService.setKeycloakLocaleIfNeeded(response.data)),
          catchError(() => of(null))
        )
        .subscribe();
    }
    return Promise.resolve();
  }

  public login(): void {
    const config = this.appConfigService.getConfig();
    if (config?.urlGatewayLogin) {
      // const relativeLoginRedirectUri = encodeURIComponent(window.location.pathname + window.location.search);
      // const redirectUrl = `${config.urlGatewayLogin}?relativeLoginRedirectUri=${relativeLoginRedirectUri}`;
      window.location.href = config?.urlGatewayLogin;
    }
  }

  public logout(): void {
    this.translocoLangDetectorService.clearKeycloakLocale();
    const config = this.appConfigService.getConfig();
    if (config?.urlGatewayLogin) {
      window.location.href = config.urlGatewayLogin;
    }

    const form = this.renderer.createElement('form');
    this.renderer.setAttribute(form, 'method', 'post');
    this.renderer.setAttribute(form, 'enctype', 'multipart/form-data');
    this.renderer.setAttribute(form, 'action', 'logout');

    const csrfInput = this.renderer.createElement('input');
    this.renderer.setAttribute(csrfInput, 'type', 'hidden');
    this.renderer.setAttribute(csrfInput, 'name', '_csrf');
    this.renderer.setAttribute(
      csrfInput,
      'value',
      document.cookie.replace(
        new RegExp(`(?:(?:^|.*;\\s*)${API_GATEWAY_EUROPE_XSRF_TOKEN}\\s*=\\s*([^;]*).*$)|^.*$`),
        '$1'
      )
    );

    this.renderer.appendChild(form, csrfInput);
    this.renderer.appendChild(document.body, form);

    this.loggerService.info(`${this.constructor.name}: 'Logging out...`);
    form.submit();
    this.loggerService.info(`${this.constructor.name}: Logout form submitted`);

    this.renderer.removeChild(document.body, form);
    this.loggerService.warn(`${this.constructor.name}: Logout form removed from DOM after submission`);
  }

  public refresh(): Promise<TokenResponse> {
    return Promise.resolve({} as TokenResponse);
  }

  public get accessToken(): string {
    return '';
  }

  public get refreshToken(): string {
    return '';
  }
}
